import * as React from 'react';
import { useMemo } from 'react';
import './UserForm.scss';
import { TK } from '../../../store/translations/translationKeys';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, Spinner } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { ChangeLog, MfaMethodEnum, PermissionEnum, UserData } from '../../../models';
import { ProfessionalEntity, UserEntity } from '../../../store/entities/state';
import { TextInputField, PermissionsField, AutocompleteTextField, ImmutableField, ComboField } from '../fields';
import { RequireAdministrator } from '../../RequirePermission';
import { useEntities } from '../../../store/entities/hooks';
import Avatar from '@material-ui/core/Avatar';
import ChangeLogInfo from '../../ChangeLogInfo';
import { useDispatch, useSelector } from 'react-redux';
import { getFeatures, getMyUser, isAdministrator } from '../../../store/session/selectors';
import { getCompanyName, getSettings } from '../../../store/settings/selectors';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import BusinessIcon from '@material-ui/icons/Business';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import WorkIcon from '@material-ui/icons/Work';
import PersonIcon from '@material-ui/icons/Person';
import EditIcon from '@material-ui/icons/Edit';
import Delete from '@material-ui/icons/Delete';
import ProfileAvatar from '../../ProfileAvatar/ProfileAvatar';
import PasswordEditPanel from '../PasswordEditPanel/PasswordEditPanel';
import EmailConfirmationBanner from '../../EmailConfirmationBanner/EmailConfirmationBanner';
import LoginMethodsInfo from '../../LoginMethodsInfo/LoginMethodsInfo';
import styled from 'styled-components';
import { entitiesLoaded, openProfessional } from '../../../store/entities/actions';
import { updateSessionUser } from '../../../store/session/actions';
import { uploadUserProfilePictureAsync, removeUserProfilePictureAsync, sendResetPasswordEmailAsync, disableAuthenticatorAsync, updateUserAsync } from '../../../ajax/requests';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import { alertSuccess, alertError } from '../../../store/alerts/actions';
import AuthenticatorSetupModal from '../../AuthenticatorSetupModal/AuthenticatorSetupModal';
import { buildAutomaticUsername, sanitizeUsername } from '../../../shared/username';
import AppButton from '../../AppButton';

const ProfessionalLinkageContainer = styled.div`
    padding: var(--spacer-S) 0;
`;

const ProfessionalLinkageRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    margin-bottom: var(--spacer-XS);
`;

const ProfessionalText = styled.span`
    color: var(--text-muted);
    font-size: 0.9rem;
`;

const ProfessionalLink = styled.span<React.HTMLAttributes<HTMLSpanElement>>`
    color: var(--appBlue);
    text-decoration: underline;
    cursor: pointer;
    font-weight: 500;

    &:hover {
        color: var(--appBlue);
        opacity: 0.8;
    }
`;

const ManagerContainer = styled.div`
    padding: var(--spacer-S) 0;
`;

const NameRow = styled.div`
    display: flex;
    gap: var(--spacer-S);
    align-items: flex-start;
`;

const NameFieldWrapper = styled.div`
    flex: 2;
`;

const SecondaryFieldWrapper = styled.div`
    flex: 1;
    min-width: 10rem;
`;

const IconButton = styled.button<React.ButtonHTMLAttributes<HTMLButtonElement>>`
    background: none;
    border: none;
    padding: var(--spacer-XXS);
    cursor: pointer;
    display: flex;
    align-items: center;
    color: var(--text-muted);

    &:hover {
        color: var(--appBlue);
    }

    svg {
        font-size: 1.2rem;
    }
`;

export type UserFormData = UserEntity & { password?: string, isPasswordValid?: boolean }

export interface UserFormStateProps {
    data: UserFormData,
    isClient?: boolean,
    hasChanged: boolean,
    isSubmitting?: boolean,
    type: 'edit' | 'create',
    canEdit: boolean,
    changeLog?: ChangeLog[],
    hidePermissions?: boolean,
    hidePictureAndName?: boolean,
}

export interface UserFormDispatchProps {
    onChange: (data: UserFormData) => void,
    onLoad?: () => void,
    onSave: (data: UserData) => void | Promise<void>,
    onCancel?: () => void,
    onDelete?: () => void,
    onSendConfirmationEmail?: () => void,
}

export type UserFormProps = UserFormStateProps & UserFormDispatchProps;

const UserForm: React.FC<UserFormProps> = (props) => {
    const {
        type,
        data,
        isClient,
        hasChanged,
        isSubmitting,
        changeLog,
        canEdit,
        onChange,
        onLoad,
        onSave,
        onCancel,
        onDelete,
        onSendConfirmationEmail,
        hidePermissions,
        hidePictureAndName,
    } = props;

    const t = useTranslations();
    const dispatch = useDispatch();
    const professionals = useEntities<ProfessionalEntity>('professionals');
    const users = useEntities<UserEntity>('users');
    const isEditing = type === 'edit';
    const [editModal, setEditModal] = React.useState(false);
    const [deleteModal, setDeleteModal] = React.useState(false);
    const [passwordExpanded, setPasswordExpanded] = React.useState(false);
    const [showProfessionalSelector, setShowProfessionalSelector] = React.useState(false);
    const [showAuthenticatorSetup, setShowAuthenticatorSetup] = React.useState(false);
    const features = useSelector(getFeatures);
    const sessionUser = useSelector(getMyUser);
    const companyName = useSelector(getCompanyName);
    const isAdmin = useSelector(isAdministrator);
    const settings = useSelector(getSettings);

    const cashRegisterSharedNames = settings.cashRegisterSettings?.sharedRegisterNames ?? [];
    const showDefaultCashRegister = useMemo(() => {
        const cashSettings = settings.cashRegisterSettings;
        if (!cashSettings?.enabled || cashRegisterSharedNames.length === 0) return false;
        if (isAdmin) return true;
        return cashSettings.nonAdminApprovers?.includes(data.username || '') ?? false;
    }, [settings.cashRegisterSettings, cashRegisterSharedNames.length, isAdmin, data.username]);

    const branchOptions = useMemo(() => {
        const branches = settings.general?.branches ?? {};
        return Object.entries(branches).map(([key, branch]) => ({ value: key, label: branch.displayName || key }));
    }, [settings.general?.branches]);
    const showBranchSelector = isAdmin && branchOptions.length > 0;

    const associatedProfessional = useMemo(() => {
        if (data.professionalId) {
            return professionals.find(p => p.id === data.professionalId);
        }
        return null;
    }, [data.professionalId, professionals]);

    const managerUser = useMemo(() => {
        if (data.managerId) {
            return users.find(u => u.id === data.managerId);
        }
        return null;
    }, [data.managerId, users]);

    const [managingIds, setManagingIds] = React.useState<string[]>([]);
    const initialManagingIds = React.useRef<string[]>([]);

    const managerOptions = useMemo(() => {
        return users
            .filter(u => u.id !== data.id && !managingIds.includes(u.id))
            .map(u => u.name || u.username);
    }, [users, data.id, managingIds]);

    const handleManagerChange = React.useCallback((userName: string) => {
        if (!userName || userName === t(TK.noneOption)) {
            onChange({ ...data, managerId: undefined });
        } else {
            const user = users.find(u => (u.name || u.username) === userName);
            if (user) {
                onChange({ ...data, managerId: user.id });
            }
        }
    }, [data, onChange, users, t]);

    React.useEffect(() => {
        if (!data.id) return;
        const ids = users.filter(u => u.managerId === data.id).map(u => u.id);
        setManagingIds(ids);
        initialManagingIds.current = ids;
    // Only re-initialize when the edited user changes, not on every users update.
    }, [data.id]);

    const managingDisplayNames = useMemo(() => {
        return managingIds
            .map(id => users.find(u => u.id === id))
            .filter((u): u is UserEntity => !!u)
            .map(u => u.name || u.username);
    }, [managingIds, users]);

    const handleManagingChange = React.useCallback((values: string[]) => {
        const ids = values
            .map(name => users.find(u => (u.name || u.username) === name)?.id)
            .filter((id): id is string => !!id);
        setManagingIds(ids);
    }, [users]);

    React.useEffect(() => { onLoad && onLoad(); }, []);

    const toggleEditModal = () => setEditModal(!editModal);

    const accountType = useMemo(() => {
        if (data.permissions?.includes(PermissionEnum.Administrator)) return t(TK.administrator);
        if (isClient) return t(TK.client);
        return t(TK.professional);
    }, [data.permissions, isClient, t]);

    const accountTypeIcon = useMemo(() => {
        if (data.permissions?.includes(PermissionEnum.Administrator)) return <SupervisorAccountIcon fontSize="small" />;
        if (isClient) return <PersonIcon fontSize="small" />;
        return <WorkIcon fontSize="small" />;
    }, [data.permissions, isClient]);

    const displayName = data.name ?? associatedProfessional?.name ?? '';
    const displayEmail = data.email ?? associatedProfessional?.email ?? '';
    const displayProfilePictureUrl = data.photoUrl ?? associatedProfessional?.photoUrl;
    const lastSuggestedUsername = React.useRef('');

    const missingEmailOrName = useMemo(() => {
        if (isEditing) return false;
        return !displayEmail.trim() && !displayName.trim();
    }, [displayEmail, displayName, isEditing]);

    const passwordDisplayValue = isEditing ? '**********' : data.password ? '**********' : '';

    // Validate Portuguese phone number for MFA SMS
    const isValidPortuguesePhone = useMemo(() => {
        if (data.mfa !== MfaMethodEnum.Sms) return true;
        if (!data.mfaPhoneNumber) return false;
        // Remove spaces, dashes, and country code prefix
        const cleaned = data.mfaPhoneNumber.replace(/[\s-]/g, '').replace(/^(\+351|00351|351)/, '');
        // Portuguese mobile numbers: 9 digits starting with 9
        return /^9\d{8}$/.test(cleaned);
    }, [data.mfa, data.mfaPhoneNumber]);

    const managingHasChanged = isEditing && (
        managingIds.length !== initialManagingIds.current.length ||
        managingIds.some(id => !initialManagingIds.current.includes(id))
    );

    const saveButtonDisabled = isSubmitting ||
        (isEditing && !hasChanged && !managingHasChanged) ||
        (!!data.password && !data.isPasswordValid) ||
        !data.username ||
        missingEmailOrName ||
        !isValidPortuguesePhone;

    const saveButtonTooltip = saveButtonDisabled && missingEmailOrName
        ? t(TK.emailOrNameRequired)
        : undefined;

    const syncSuggestedUsername = React.useCallback((nextData: UserFormData) => {
        if (isEditing) {
            return;
        }

        const suggestedUsername = buildAutomaticUsername(nextData.email, nextData.name, companyName);
        const canOverwriteUsername = !nextData.username || nextData.username === lastSuggestedUsername.current;

        lastSuggestedUsername.current = suggestedUsername;

        if (!canOverwriteUsername || nextData.username === suggestedUsername) {
            return;
        }

        onChange({ ...nextData, username: suggestedUsername });
    }, [isEditing, onChange, companyName]);

    const handleProfessionalChange = React.useCallback((professionalName: string) => {
        if (professionalName === t(TK.noneOption) || !professionalName) {
            onChange({ ...data, professionalId: undefined });
        } else {
            const professional = professionals.find(p => p.name === professionalName);
            if (professional) {
                const updates: Partial<UserFormData> = { professionalId: professional.id };

                if (professional.name) {
                    updates.name = professional.name;
                }

                if (professional.email) {
                    updates.email = professional.email;
                }

                // When creating a new user, pre-fill fields from professional
                if (!isEditing) {
                    // Pre-fill photoUrl
                    if (!data.photoUrl && professional.photoUrl) {
                        updates.photoUrl = professional.photoUrl;
                    }
                    if (!data.photoFileId && professional.photoFileId) {
                        updates.photoFileId = professional.photoFileId;
                    }
                }

                onChange({ ...data, ...updates });
            }
        }
        setShowProfessionalSelector(false);
    }, [data, onChange, professionals, t, isEditing]);

    const handleSave = React.useCallback(async () => {
        const result = onSave(data);
        if (!isEditing) return;

        if (result && typeof (result as Promise<void>).then === 'function') {
            try {
                await (result as Promise<void>);
                if (passwordExpanded) {
                    setPasswordExpanded(false);
                    onChange({ ...data, password: undefined, isPasswordValid: true });
                }
            } catch {
                // Error is already handled by updateEntity action
                return;
            }
        }

        if (!data.id) return;
        const added = managingIds.filter(id => !initialManagingIds.current.includes(id));
        const removed = initialManagingIds.current.filter(id => !managingIds.includes(id));
        if (added.length === 0 && removed.length === 0) return;

        await Promise.all([
            ...added.map(async id => {
                const user = users.find(u => u.id === id);
                if (!user) return;
                await updateUserAsync(id, { id, managerId: data.id });
                const updated: UserEntity = { ...user, managerId: data.id };
                dispatch(entitiesLoaded('users', [updated], [], 'AppendResult'));
            }),
            ...removed.map(async id => {
                const user = users.find(u => u.id === id);
                if (!user) return;
                await updateUserAsync(id, { id, managerId: undefined });
                const updated: UserEntity = { ...user, managerId: undefined };
                dispatch(entitiesLoaded('users', [updated], [], 'AppendResult'));
            }),
        ]);
        initialManagingIds.current = [...managingIds];
    }, [data, isEditing, onSave, passwordExpanded, onChange, managingIds, users, dispatch]);

    const handlePasswordToggle = React.useCallback(() => {
        if (passwordExpanded) {
            // Closing: clear password if it was being edited
            onChange({ ...data, password: undefined, isPasswordValid: true });
        }
        setPasswordExpanded(!passwordExpanded);
    }, [passwordExpanded, data, onChange]);

    const handleAvatarUpload = (url: string, photoFileId: string) => {
        const updatedUser = {
            ...data,
            photoUrl: url,
            photoFileId,
        };

        onChange(updatedUser);
        dispatch(entitiesLoaded('users', [updatedUser], [], 'AppendResult'));

        if (associatedProfessional) {
            const updatedProfessional = {
                ...associatedProfessional,
                photoUrl: url,
                photoFileId,
            };
            dispatch(entitiesLoaded('professionals', [updatedProfessional], [], 'AppendResult'));
        }

        if (sessionUser?.id === data.id) {
            dispatch(updateSessionUser({ photoUrl: url }));
        }
    };

    const handleAvatarRemove = React.useCallback(async () => {
        if (!data.id) {
            return Promise.reject(new Error('User id is required to remove avatar'));
        }

        await removeUserProfilePictureAsync(data.id);

        const updatedUser = {
            ...data,
            photoUrl: '',
            photoFileId: undefined,
        };

        onChange(updatedUser);
        dispatch(entitiesLoaded('users', [updatedUser], [], 'AppendResult'));

        if (associatedProfessional) {
            const updatedProfessional = {
                ...associatedProfessional,
                photoUrl: '',
                photoFileId: undefined,
            };
            dispatch(entitiesLoaded('professionals', [updatedProfessional], [], 'AppendResult'));
        }

        if (sessionUser?.id === data.id) {
            dispatch(updateSessionUser({ photoUrl: '' }));
        }
    }, [data, onChange, dispatch, associatedProfessional, sessionUser]);

    const handleSendResetPasswordEmail = React.useCallback(async () => {
        if (!data.username) {
            dispatch(alertError(t(TK.somethingWentWrong)));
            return;
        }

        try {
            await sendResetPasswordEmailAsync({ username: data.username, isClient: false });
            dispatch(alertSuccess(t(TK.emailSentSuccessfully)));
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.response?.data?.message || t(TK.somethingWentWrong);
            dispatch(alertError(errorMessage));
        }
    }, [data.username, dispatch, t]);

    // Check if session user is admin viewing another user's profile
    const isAdminViewingOtherUser = React.useMemo(() => {
        return sessionUser?.permissions?.includes(PermissionEnum.Administrator) &&
               sessionUser?.id !== data.id &&
               isEditing;
    }, [sessionUser, data.id, isEditing]);

    const formActions = (
            <div className="userForm__buttonsContainer profileActions">
            <div className="buttonGroup">
                { isEditing && canEdit && onDelete && (
                    <AppButton id={'user-delete-button'} outline color="danger" onClick={() => setDeleteModal(true)}>
                        <Delete />
                    </AppButton>
                )}
                { onCancel && !(passwordExpanded && isEditing) && (
                    <AppButton id={'user-cancel-button'} onClick={onCancel}>{t(TK.cancel)}</AppButton>
                ) }
                { passwordExpanded && isEditing && (
                    <AppButton id={'user-password-cancel-button'} onClick={handlePasswordToggle}>
                        {t(TK.cancel)}
                    </AppButton>
                )}
                <span title={saveButtonTooltip}>
                    <AppButton
                        id={'user-save-button'}
                        color="success"
                        title={saveButtonTooltip}
                        disabled={saveButtonDisabled}
                        onClick={() => isEditing && data.password ? setEditModal(true) : handleSave()}
                        style={saveButtonTooltip ? { pointerEvents: 'none' } : undefined}
                    >
                        {isSubmitting && <Spinner size="sm" color="secundary"/>}
                        {isEditing ? t(TK.saveChanges) : t(TK.create)}
                    </AppButton>
                </span>
            </div>
            { !!changeLog?.length && <ChangeLogInfo changeLog={changeLog}/> }
        </div>
    );

    return (
        <>
            <Form className="profileForm">
                {!hidePictureAndName && (
                    <section className="profileSection">
                        <div className="profileHeader">
                            <ProfileAvatar
                                    src={displayProfilePictureUrl}
                                    alt={displayName || data.username}
                                    displayName={displayName}
                                    username={data.username}
                                    email={data.email}
                                    isEditable={isEditing}
                                    onUploadSuccess={handleAvatarUpload}
                                    onRemove={handleAvatarRemove}
                                    uploadHandler={(file) => uploadUserProfilePictureAsync(data.id, file)}
                                    accountType={isEditing ? accountType : undefined}
                                    accountTypeIcon={isEditing ? accountTypeIcon : undefined}
                                    editLabel={t(TK.edit)}
                                    removeLabel={t(TK.remove)}
                                    onlyImageFilesAllowedMessage={t(TK.onlyImageFilesAllowed)}
                                    fileSizeLimitMessage={t(TK.fileSizeMustBeLessThan2MB)}
                                    profilePictureUpdatedMessage={t(TK.profilePictureUpdated)}
                                    profilePictureRemovedMessage={t(TK.profilePictureRemoved)}
                                    errorUploadingMessage={t(TK.errorUploadingProfilePicture)}
                                />
                                <div className="profileFields">
                                    <NameRow>
                                        <NameFieldWrapper>
                                            <TextInputField
                                                id={'user-name'}
                                                label={t(TK.fullName)}
                                                value={displayName}
                                                onChange={name => onChange({
                                                    ...data,
                                                    name,
                                                })}
                                                onBlur={() => syncSuggestedUsername(data)}
                                                inputStyle={{ fontWeight: 'bold' }}
                                                invalid={missingEmailOrName}
                                                description={missingEmailOrName ? t(TK.emailOrNameRequired) : undefined}
                                            />
                                        </NameFieldWrapper>
                                        {showBranchSelector && (
                                            <SecondaryFieldWrapper>
                                                <AutocompleteTextField
                                                    id={'user-branches'}
                                                    label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25em' }}><BusinessIcon style={{ fontSize: '1rem' }} />{t(TK.userBranches)}</span>}
                                                    helpText={t(TK.userBranchesHelp)}
                                                    multiSelect
                                                    values={(data.branchIds ?? []).map(id => branchOptions.find(o => o.value === id)?.label ?? id)}
                                                    options={branchOptions.map(o => o.label)}
                                                    onValuesChanged={labels => {
                                                        const ids = labels.map(label => branchOptions.find(o => o.label === label)?.value ?? label);
                                                        onChange({ ...data, branchIds: ids });
                                                    }}
                                                />
                                            </SecondaryFieldWrapper>
                                        )}
                                    </NameRow>
                                    <NameRow>
                                        <NameFieldWrapper>
                                            <TextInputField
                                                id={'user-email'}
                                                label={t(TK.email)}
                                                type="email"
                                                helpText={t(TK.userEmailLoginHelp)}
                                                value={displayEmail}
                                                onChange={email => onChange({...data, email})}
                                                onBlur={() => syncSuggestedUsername(data)}
                                                invalid={missingEmailOrName}
                                                description={missingEmailOrName ? t(TK.emailOrNameRequired) : undefined}
                                            />
                                        </NameFieldWrapper>
                                        {showDefaultCashRegister && (
                                            <SecondaryFieldWrapper>
                                                <ComboField
                                                    id={'user-default-cash-register'}
                                                    label={t(TK.cashRegisterDefaultRegister)}
                                                    labelIcon={<PointOfSaleIcon style={{ fontSize: '1rem' }} />}
                                                    helpText={t(TK.cashRegisterDefaultRegisterHelp)}
                                                    value={data.defaultCashRegisterName ?? ''}
                                                    options={[
                                                        { value: '', label: t(TK.cashRegisterOwnRegister) },
                                                        ...cashRegisterSharedNames.map(name => ({ value: name, label: name })),
                                                    ]}
                                                    onChange={value => onChange({ ...data, defaultCashRegisterName: value || undefined })}
                                                />
                                            </SecondaryFieldWrapper>
                                        )}
                                    </NameRow>
                                    <RequireAdministrator>
                                        <>
                                        <ProfessionalLinkageContainer>
                                            {associatedProfessional ? (
                                                <>
                                                    <ProfessionalLinkageRow>
                                                        <ProfessionalText>
                                                            {t(TK.userAssociatedWithProfessional)}:{' '}
                                                            <ProfessionalLink
                                                                onClick={() => dispatch(openProfessional(
                                                                    associatedProfessional.id,
                                                                    'data',
                                                                ))}
                                                            >
                                                                {associatedProfessional.name}
                                                            </ProfessionalLink>
                                                        </ProfessionalText>
                                                        <IconButton
                                                            type="button"
                                                            onClick={() => setShowProfessionalSelector(!showProfessionalSelector)}
                                                            title={t(TK.edit)}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </ProfessionalLinkageRow>
                                                    {showProfessionalSelector && (
                                                        <AutocompleteTextField
                                                            id={'user-professional-selector'}
                                                            placeholder={t(TK.selectProfessional)}
                                                            value={associatedProfessional.name}
                                                            options={[t(TK.noneOption), ...professionals.map(p => p.name)]}
                                                            onValueChanged={handleProfessionalChange}
                                                        />
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <AppButton
                                                        id="associate-professional-button"
                                                        color="link"
                                                        onClick={() => setShowProfessionalSelector(true)}
                                                        style={{ padding: 0 }}
                                                    >
                                                        {t(TK.associateUserWithProfessional)}
                                                    </AppButton>
                                                    {showProfessionalSelector && (
                                                        <AutocompleteTextField
                                                            id={'user-professional-selector'}
                                                            placeholder={t(TK.selectProfessional)}
                                                            value=""
                                                            options={professionals.map(p => p.name)}
                                                            onValueChanged={handleProfessionalChange}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </ProfessionalLinkageContainer>
                                    <ManagerContainer>
                                        <AutocompleteTextField
                                            id={'user-manager-selector'}
                                            label={t(TK.manager)}
                                            helpText={t(TK.managerHelpText)}
                                            value={managerUser ? (managerUser.name || managerUser.username) : ''}
                                            options={[t(TK.noneOption), ...managerOptions]}
                                            onValueChanged={handleManagerChange}
                                            startAdornment={managerUser && (
                                                <Avatar
                                                    src={managerUser.photoUrl || undefined}
                                                    style={{ width: 24, height: 24, marginRight: 'var(--spacer-XS)', fontSize: '0.75rem' }}
                                                >
                                                    {(managerUser.name || managerUser.username || '').charAt(0).toUpperCase()}
                                                </Avatar>
                                            )}
                                            renderItemIcon={(option) => {
                                                const user = users.find(u => (u.name || u.username) === option);
                                                return user?.photoUrl
                                                    ? <Avatar src={user.photoUrl} style={{ width: 24, height: 24 }} />
                                                    : null;
                                            }}
                                        />
                                    </ManagerContainer>
                                    {isEditing && (
                                        <AutocompleteTextField
                                            id={'user-managing-selector'}
                                            label={t(TK.managing)}
                                            multiSelect
                                            values={managingDisplayNames}
                                            options={users.filter(u => u.id !== data.id && u.id !== data.managerId).map(u => u.name || u.username)}
                                            onValuesChanged={handleManagingChange}
                                            renderItemIcon={(option) => {
                                                const user = users.find(u => (u.name || u.username) === option);
                                                return user?.photoUrl
                                                    ? <Avatar src={user.photoUrl} style={{ width: 24, height: 24 }} />
                                                    : null;
                                            }}
                                        />
                                    )}
                                        </>
                                    </RequireAdministrator>
                                </div>
                            </div>

                            <EmailConfirmationBanner
                                email={data.email}
                                emailConfirmed={data.emailConfirmed}
                                message={t(TK.emailNotConfirmedMessage)}
                                resendButtonLabel={t(TK.resendConfirmationEmail)}
                                onResendEmail={onSendConfirmationEmail}
                            />
                    </section>
                )}

                <div className="sectionHeader">
                    <h5 className="sectionTitle">{t(TK.accountSecurity)}</h5>
                </div>
                <div className="subSection">
                    <TextInputField
                        id={'user-username'}
                        label={t(TK.usernameLogin)}
                        value={data.username || ''}
                        onChange={username => onChange({...data, username: sanitizeUsername(username)})}
                        disabled={isEditing}
                        description={isEditing ? t(TK.usernameLoginPrimaryHint) : undefined}
                    />

                    {isEditing && (
                        <div className="passwordFieldRow">
                            <TextInputField
                                id={'user-password-display'}
                                label={t(TK.password)}
                                type="password"
                                value={passwordDisplayValue}
                                readOnly
                                endAdornment={(
                                    <IconButton
                                        type="button"
                                        onClick={handlePasswordToggle}
                                        title={t(TK.changePassword)}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                )}
                            />
                        </div>
                    )}

                    {isEditing && (
                        <div className="passwordActions">
                            <LoginMethodsInfo
                                message={t(TK.changePassword)}
                                icon={<EditIcon style={{ fontSize: '1rem' }} />}
                                onClick={handlePasswordToggle}
                            />
                            {isAdminViewingOtherUser && (
                                <>
                                    <span className="passwordActionsSeparator">|</span>
                                    <LoginMethodsInfo
                                        message={t(TK.sendNewPasswordByEmail)}
                                        icon={<MailOutlineIcon style={{ fontSize: '1rem' }} />}
                                        onClick={handleSendResetPasswordEmail}
                                    />
                                </>
                            )}
                        </div>
                    )}

                    {(passwordExpanded || !isEditing) && (
                        <PasswordEditPanel
                            password={data.password}
                            isPasswordValid={data.isPasswordValid}
                            isEditing={isEditing}
                            isSubmitting={isSubmitting}
                            label={t(TK.newPassword)}
                            confirmLabel={t(TK.confirmPassword)}
                            placeholder={t(TK.newPassword)}
                            confirmPlaceholder={t(TK.confirmPassword)}
                            passwordsMustMatchText={t(TK.passwordsMustMatch)}
                            cancelLabel={t(TK.cancel)}
                            saveLabel={t(TK.saveChanges)}
                            infoText={(!isEditing && t(TK.newPasswordEmailSentIfPasswordIsEmpty)) || undefined}
                            infoAsText={!isEditing}
                            hideActions={true}
                            onPasswordChange={(password, valid) => onChange({...data, password, isPasswordValid: valid})}
                            onCancel={handlePasswordToggle}
                            onSave={() => isEditing && data.password ? setEditModal(true) : handleSave()}
                        />
                    )}

                    {!isAdminViewingOtherUser && (
                        <LoginMethodsInfo message={t(TK.signInMethodsAvailable)} />
                    )}
                    <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', marginTop: 'var(--spacer-M)'}}>
                        <ComboField
                            id={'user-mfa'}
                            label={t(TK.twoFactorAuthentication)}
                            helpText={t(TK.twoFactorAuthenticationHelp)}
                            value={[t(TK.none), t(TK.sms), t(TK.email), t(TK.authenticator)][data.mfa ?? MfaMethodEnum.None]}
                            options={[
                                t(TK.none),
                                t(TK.sms),
                                { value: t(TK.email), disabled: true },
                                { value: t(TK.authenticator), label: `${t(TK.authenticator)} (${t(TK.recommended)})`},
                            ]}
                            onChange={value => {
                                const mfaOptions = [t(TK.none), t(TK.sms), t(TK.email), t(TK.authenticator)];
                                const mfa = mfaOptions.indexOf(value ?? t(TK.none)) as MfaMethodEnum;
                                onChange({...data, mfa});
                            }}
                        />

                        {data.mfa === MfaMethodEnum.Sms && (
                            <TextInputField
                                id={'user-mfa-phone'}
                                label={t(TK.phoneNumber)}
                                value={data.mfaPhoneNumber || ''}
                                onChange={mfaPhoneNumber => onChange({...data, mfaPhoneNumber})}
                                invalid={!isValidPortuguesePhone}
                                description={!isValidPortuguesePhone ? t(TK.invalidPortuguesePhoneNumber) : undefined}
                            />
                        )}

                        {data.mfa === MfaMethodEnum.Authenticator && isEditing && sessionUser?.id === data.id && (
                            <div style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-S)'}}>
                                {data.mfaAuthenticatorConfigured ? (
                                    <>
                                        <span style={{color: 'var(--success-color)', fontSize: '0.9rem'}}>
                                            {t(TK.authenticatorConfigured)}
                                        </span>
                                        <AppButton
                                            size="sm"
                                            color="danger"
                                            outline
                                            onClick={async () => {
                                                try {
                                                    await disableAuthenticatorAsync();
                                                    onChange({...data, mfaAuthenticatorConfigured: false});
                                                    dispatch(alertSuccess(t(TK.authenticatorDisabled)));
                                                } catch {
                                                    dispatch(alertError(t(TK.somethingWentWrong)));
                                                }
                                            }}
                                        >
                                            {t(TK.disableAuthenticator)}
                                        </AppButton>
                                    </>
                                ) : (
                                    <>
                                        <span style={{color: 'var(--warning-color)', fontSize: '0.9rem'}}>
                                            {t(TK.authenticatorNotConfigured)}
                                        </span>
                                        <AppButton
                                            size="sm"
                                            color="primary"
                                            onClick={() => setShowAuthenticatorSetup(true)}
                                        >
                                            {t(TK.setupAuthenticator)}
                                        </AppButton>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <AuthenticatorSetupModal
                        isOpen={showAuthenticatorSetup}
                        onClose={() => setShowAuthenticatorSetup(false)}
                        onSetupComplete={() => {
                            onChange({...data, mfaAuthenticatorConfigured: true});
                        }}
                    />
                </div>

                {!hidePermissions && (
                    <RequireAdministrator>
                        <div className="subSection">
                            <PermissionsField
                                id={'user-permissions'}
                                label={t(TK.permissions)}
                                values={data.permissions || []}
                                isProfessional={!!data.professionalId}
                                features={features}
                                onChange={permissions => onChange({...data, permissions})}
                            />
                        </div>
                    </RequireAdministrator>
                )}

                {formActions}
            </Form>

            <Modal isOpen={editModal} toggle={toggleEditModal}>
                <ModalHeader toggle={toggleEditModal}>{t(TK.confirmPasswordChange)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToChangePassword)}</ModalBody>
                <ModalFooter>
                    <AppButton id={'user-are-you-sure-cancel-button'} color="secondary" onClick={toggleEditModal}>{t(TK.cancel)}</AppButton>
                    <AppButton id={'user-are-you-sure-confirm-button'} className="primaryActionButton" disabled={isSubmitting} onClick={() => { handleSave(); toggleEditModal(); }}>{t(TK.continueButton)}</AppButton>
                </ModalFooter>
            </Modal>

            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)}>
                <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                <ModalFooter>
                    <AppButton id={'user-confirm-delete-button'} color="danger" onClick={() => { onDelete && onDelete(); setDeleteModal(false); }}>{t(TK.delete)}</AppButton>{' '}
                    <AppButton id={'user-abort-delete-button'} color="secondary" onClick={() => setDeleteModal(false)}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default UserForm;
